import React, { useEffect, useRef, useState } from 'react';

// US-format date field (mm/dd/yyyy) that works regardless of the browser locale.
//
// The native <input type="date"> renders its display format from the browser's language
// setting (not the element's lang attribute), so it can show dd-mm-yyyy for non-US users.
// This component instead shows a masked TEXT input in mm/dd/yyyy and keeps a native date
// picker (opened by the calendar button) for convenience. The value it emits via onChange
// is always ISO yyyy-mm-dd (or '' when empty/incomplete), so callers/backends are unchanged.

const isoToUS = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

// Format a raw digit string (mmddyyyy) into mm/dd/yyyy as the user types.
const formatUSTyping = (digits) => {
    const d = digits.slice(0, 8);
    let out = d.slice(0, 2);
    if (d.length >= 3) out += '/' + d.slice(2, 4);
    if (d.length >= 5) out += '/' + d.slice(4, 8);
    return out;
};

const usToIso = (formatted) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formatted);
    if (!m) return '';
    const [, mm, dd, yyyy] = m;
    const month = Number(mm), day = Number(dd), year = Number(yyyy);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return '';
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    // Reject impossible dates (e.g. 02/31) — Date rolls them over, so verify the parts stuck.
    if (Number.isNaN(d.getTime()) || d.getMonth() + 1 !== month || d.getDate() !== day) return '';
    return `${yyyy}-${mm}-${dd}`;
};

export default function USDateInput({
    value = '',
    onChange,
    min,
    max,
    className = '',
    placeholder = 'mm/dd/yyyy',
    'aria-label': ariaLabel = 'Date (mm/dd/yyyy)',
    disabled = false,
}) {
    const [text, setText] = useState(isoToUS(value));
    const nativeRef = useRef(null);

    // Keep the visible text in sync when the ISO value changes from outside (prefill/reset).
    useEffect(() => { setText(isoToUS(value)); }, [value]);

    const handleText = (event) => {
        const digits = event.target.value.replace(/[^\d]/g, '');
        const formatted = formatUSTyping(digits);
        setText(formatted);
        if (digits.length === 0) {
            onChange('');
        } else if (digits.length === 8) {
            onChange(usToIso(formatted)); // '' if the typed date is invalid
        }
        // Partial input: don't emit yet (keeps the parent's ISO value empty until complete).
    };

    const openPicker = () => {
        const el = nativeRef.current;
        if (!el || disabled) return;
        if (typeof el.showPicker === 'function') {
            try { el.showPicker(); return; } catch (_) { /* fall through */ }
        }
        el.focus();
        el.click();
    };

    return (
        <div className="relative">
            <input
                type="text"
                inputMode="numeric"
                value={text}
                onChange={handleText}
                placeholder={placeholder}
                aria-label={ariaLabel}
                disabled={disabled}
                className={className}
            />
            {/* Calendar button opens the native picker; the hidden native input holds the ISO
                value and enforces min/max in the picker. */}
            <button
                type="button"
                onClick={openPicker}
                disabled={disabled}
                aria-label="Open calendar"
                className="absolute inset-y-0 right-2 my-auto flex h-7 w-7 items-center justify-center rounded-md text-[#414141] hover:bg-black/5 disabled:opacity-40"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
            </button>
            <input
                ref={nativeRef}
                type="date"
                value={value || ''}
                min={min}
                max={max}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                tabIndex={-1}
                aria-hidden="true"
                className="pointer-events-none absolute right-2 bottom-0 h-0 w-0 opacity-0"
            />
        </div>
    );
}
